/*
 * Copyright Mission 42 Ventures, Inc. All rights reserved.
 * Unauthorized copying, distribution, or use is prohibited.
 */

import { useEffect, useState } from 'react';
import {
  discoveryApiRef,
  errorApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  ReviewQualityData,
  parseReviewQualityResponse,
} from './claudeReviewQuality';

/**
 * Reads the review-quality finding rows for the trailing window (PLAT-1819).
 *
 * Same degradation contract as useClaudeActivity: the card is a companion to
 * the usage panel, so losing it must not cost the panel — but the failure is
 * REPORTED (errorApi), not swallowed. A 404 stays silent: a server one deploy
 * behind has no /review-quality route yet, and the rollout window must not
 * toast an error at every admin.
 */
export const useClaudeReviewQuality = (
  reload = 0,
  days = 90,
): { data?: ReviewQualityData } => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const errorApi = useApi(errorApiRef);
  const [data, setData] = useState<ReviewQualityData>();

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const base = await discoveryApi.getBaseUrl('claude-metrics');
        const res = await fetchApi.fetch(
          `${base}/review-quality?${new URLSearchParams({ days: String(days) })}`,
        );
        if (res.status === 404) return;
        if (!res.ok) throw new Error(`request failed (${res.status})`);
        const parsed = parseReviewQualityResponse(await res.json());
        if (!parsed) throw new Error('review-quality payload unreadable');
        if (active) setData(parsed);
      } catch (e) {
        if (active) setData(undefined);
        errorApi.post(
          new Error(
            `Claude usage: review quality unavailable (${
              e instanceof Error ? e.message : e
            })`,
          ),
        );
      }
    })();
    return () => {
      active = false;
    };
  }, [discoveryApi, fetchApi, errorApi, reload, days]);

  return { data };
};
